"use client"

import { useState } from "react"
import { Bell, Check, Loader2 } from "lucide-react"

type Props = {
  openCallId: string
  source?: "residency_page" | "open_call_card" | "feed"
  /** Compact variant for tight spots (cards); default is the full inline form. */
  compact?: boolean
}

/**
 * Free, no-account "remind me before this closes" email capture. The
 * high-intent conversion entry point for logged-out visitors — they get one
 * reminder before the deadline, with a Saver upsell in the confirmation email.
 */
export function FreeReminderForm({ openCallId, source = "residency_page", compact = false }: Props) {
  const [email, setEmail] = useState("")
  const [website, setWebsite] = useState("") // honeypot
  const [status, setStatus] = useState<"idle" | "loading" | "done" | "error">("idle")
  const [message, setMessage] = useState("")

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!email.trim()) return
    setStatus("loading")
    setMessage("")
    try {
      const res = await fetch("/api/reminders", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), openCallId, source, website }),
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok) {
        setStatus("done")
        setMessage(data.alreadySet ? "You're already on the list for this one." : "Done — check your inbox to confirm.")
      } else {
        setStatus("error")
        setMessage(data.error ?? "Something went wrong. Try again.")
      }
    } catch {
      setStatus("error")
      setMessage("Network error. Try again.")
    }
  }

  if (status === "done") {
    return (
      <div className={`flex items-center gap-2 text-[13px] text-green-700 ${compact ? "" : "px-3.5 py-2.5 bg-green-50 border border-green-200 rounded-lg"}`}>
        <Check size={14} className="shrink-0" />
        <span>{message}</span>
      </div>
    )
  }

  return (
    <form onSubmit={submit} className={compact ? "flex flex-col gap-1.5" : "flex flex-col gap-2"}>
      {!compact && (
        <label className="flex items-center gap-1.5 text-[13px] font-medium text-[var(--foreground)]">
          <Bell size={13} className="text-amber-600" />
          Remind me before this closes
        </label>
      )}
      <div className="flex gap-2">
        {/* honeypot — visually hidden, off-screen, not tab-focusable */}
        <input
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          className="absolute -left-[9999px] h-0 w-0 opacity-0"
          aria-hidden="true"
        />
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@email.com"
          disabled={status === "loading"}
          className="flex-1 min-w-0 px-3 py-2 text-[13px] border border-[var(--border)] rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:opacity-60"
        />
        <button
          type="submit"
          disabled={status === "loading"}
          className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 text-[13px] font-medium bg-[var(--foreground)] text-white rounded-md hover:opacity-90 transition-opacity disabled:opacity-60"
        >
          {status === "loading" ? <Loader2 size={13} className="animate-spin" /> : <Bell size={13} />}
          {compact ? "Remind me" : "Remind me"}
        </button>
      </div>
      {status === "error" && <p className="text-[12px] text-red-600">{message}</p>}
      {!compact && (
        <p className="text-[11px] text-[var(--muted-foreground)]">
          One free reminder, no account needed. Want alerts on every deadline?{" "}
          <a href="/pro" className="underline hover:text-[var(--foreground)]">Go Saver</a>.
        </p>
      )}
    </form>
  )
}
